import { useState, useRef, useEffect } from "react";

export default function Contato() {
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [charCount, setCharCount] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".cont-item").forEach((item, i) => {
              setTimeout(() => item.classList.add("cont-visible"), i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector("textarea");
    if (textarea && textarea.value.length > 500) {
      return;
    }

    // Honeypot check — bot preencheu, finge sucesso
    const honeypot = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypot && honeypot.value.trim() !== "") {
      setStatus("success");
      form.reset();
      setCharCount(0);
      return;
    }

    setStatus("sending");
    const data = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      if (key !== "phone_alt") data.append(key, value as string);
    });
    try {
      const res = await fetch("https://formsubmit.co/ajax/contato@ferrarezi.com.br", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <section
      id="contato"
      className="py-16 lg:py-20 relative overflow-hidden"
      ref={sectionRef}
    >
      {/* Background image — desktop */}
      <img
        src="https://storage.readdy-site.link/project_files/9309375e-f328-4fba-b8d7-64d73f28a855/9643d223-d0e5-4b5e-bb22-615dcc800df4_compressed_WhatsApp-Image-2026-07-14-at-15.22.10.webp"
        alt=""
        aria-hidden="true"
        className="hidden md:block absolute inset-0 w-full h-full object-cover object-center"
      />

      {/* Background image — mobile */}
      <img
        src="https://storage.readdy-site.link/project_files/9309375e-f328-4fba-b8d7-64d73f28a855/0c8088ab-59c7-4eff-adf1-0c22449d5cec_compressed_WhatsApp-Image-2026-07-14-at-13.54.05.webp"
        alt=""
        aria-hidden="true"
        className="md:hidden absolute inset-0 w-full h-full object-cover object-center"
      />

      {/* Bottom gradient to blend with next section */}
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-white via-white/60 to-transparent z-[1]" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left */}
          <div className="cont-item opacity-0 translate-y-6 transition-all duration-500">
            <div className="inline-flex items-center gap-2 mb-5">
              <span className="w-6 h-px bg-black" />
              <span className="text-black text-xs font-semibold tracking-widest uppercase">
                Fale conosco
              </span>
            </div>

            {/* Avião Ferrarezi */}
            <div className="mb-5">
              <img
                src="https://storage.readdy-site.link/project_files/9309375e-f328-4fba-b8d7-64d73f28a855/8a9e5432-c94d-4bc6-b990-fbbc2eca2551_compressed_Mascotee.pdf-1.webp"
                alt="Avião Ferrarezi"
                className="w-full h-auto object-contain"
              />
            </div>

            {/* Título e descrição abaixo do avião */}
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-black leading-tight mb-4">
              Venha decolar com a Ferrarezi
            </h2>
            <p className="text-black/70 text-lg leading-relaxed">
              Preencha o formulário e um especialista entrará em contato em até 24 horas para entender suas necessidades.
            </p>
          </div>

          {/* Right: Form */}
          <div className="cont-item opacity-0 translate-y-6 transition-all duration-500">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-7 md:p-9 border border-white/40 shadow-sm">
              {status === "success" ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#FBC52D]/20 mb-5">
                    <i className="ri-check-line text-[#FBC52D] text-3xl" />
                  </div>
                  <h3 className="text-black font-bold text-xl mb-2">Mensagem enviada!</h3>
                  <p className="text-black/70 text-sm">
                    Obrigado pelo contato. Nossa equipe retornará em até 24 horas.
                  </p>
                  <button
                    onClick={() => setStatus("idle")}
                    className="whitespace-nowrap mt-6 bg-black text-white text-sm font-semibold px-6 py-2.5 rounded-full hover:bg-gray-900 transition-colors cursor-pointer"
                  >
                    Enviar outra mensagem
                  </button>
                </div>
              ) : (
                <form
                  id="contato-ferrarezi"
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-4"
                >
                  <input type="hidden" name="_subject" value="Contato | Site Ferrarezi" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Nome *</label>
                      <input
                        type="text"
                        name="nome"
                        required
                        placeholder="Seu nome completo"
                        className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-black placeholder-gray-400 focus:outline-none focus:border-[#FBC52D] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Empresa *</label>
                      <input
                        type="text"
                        name="empresa"
                        required
                        placeholder="Nome da empresa"
                        className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-black placeholder-gray-400 focus:outline-none focus:border-[#FBC52D] transition-colors"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Telefone *</label>
                      <input
                        type="tel"
                        name="telefone"
                        required
                        placeholder="(11) 99999-9999"
                        className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-black placeholder-gray-400 focus:outline-none focus:border-[#FBC52D] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">E-mail *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="seu@email.com"
                        className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-black placeholder-gray-400 focus:outline-none focus:border-[#FBC52D] transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      Mensagem
                      <span className="text-black/60 font-normal ml-1">({charCount}/500)</span>
                    </label>
                    <textarea
                      name="mensagem"
                      rows={4}
                      maxLength={500}
                      placeholder="Conte um pouco sobre sua empresa e o que você precisa..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-black placeholder-gray-400 focus:outline-none focus:border-[#FBC52D] transition-colors resize-none"
                    />
                  </div>

                  {/* Honeypot anti-spam */}
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="absolute opacity-0 pointer-events-none"
                  />

                  {status === "error" && (
                    <p className="text-red-500 text-xs">Ocorreu um erro. Tente novamente ou entre em contato pelo WhatsApp.</p>
                  )}

                  <button
                    type="submit"
                    disabled={status === "sending"}
                    className="whitespace-nowrap w-full bg-[#FBC52D] text-black font-semibold py-3.5 rounded-xl hover:bg-[#d4a800] transition-all duration-200 cursor-pointer text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {status === "sending" ? "Enviando..." : "Enviar mensagem"}
                  </button>

                  <p className="text-black/60 text-xs text-center">
                    Seus dados estão seguros. Não compartilhamos informações com terceiros.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Full-width contact strip */}
        <div className="mt-16 pt-10 border-t border-gray-200">
          <div className="flex flex-col md:flex-row justify-center items-start md:items-center gap-4 md:gap-10">
            {[
              { icon: "ri-map-pin-line", label: "Localização", value: "São Paulo, SP, Brasil" },
              { icon: "ri-phone-line", label: "Telefone", value: "+55 11 93618-9998" },
              { icon: "ri-mail-line", label: "E-mail", value: "contato@ferrarezi.com.br" },
            ].map((info) => (
              <div key={info.label} className="flex items-center gap-3 w-full md:w-auto">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#FBC52D]/10 shrink-0">
                  <i className={`${info.icon} text-[#FBC52D] text-base`} />
                </div>
                <div>
                  <div className="text-xs text-black/60 font-medium">{info.label}</div>
                  <div className="text-black text-sm font-medium">{info.value}</div>
                </div>
              </div>
            ))}
          </div>

          {/* WhatsApp Button */}
          <div className="flex justify-center mt-8">
            <a
              href="https://wa.me/5511936189998"
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap inline-flex items-center gap-3 bg-[#25D366] text-white font-semibold px-7 py-3.5 rounded-full hover:bg-[#1ebe5d] transition-all duration-200 cursor-pointer text-sm"
            >
              <i className="ri-whatsapp-line text-lg" />
              Falar pelo WhatsApp
            </a>
          </div>
        </div>
      </div>

      <style>{`
        .cont-visible {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
      `}</style>
    </section>
  );
}